import { useRef, useState } from 'react'
import { Award, Check, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import ConfirmDialog from '@/components/ui/confirm-dialog'
import type { OpenBadge } from '@/types'

type Props = {
  badge: OpenBadge
  onClick?: () => void
  onDelete: (id: number) => void
  isSelecting?: boolean
  isSelected?: boolean
  onSelect?: (id: number) => void
  onLongPress?: () => void
}

export default function BadgeCard({ badge, onClick, onDelete, isSelecting, isSelected, onSelect, onLongPress }: Props) {
  const [confirmOpen, setConfirmOpen] = useState(false)
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handlePointerDown = () => {
    if (!isSelecting && onLongPress) {
      longPressTimer.current = setTimeout(() => {
        onLongPress()
        onSelect?.(badge.id)
      }, 500)
    }
  }
  const handlePointerUp = () => {
    if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null }
  }

  return (
    <div
      className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer select-none transition-colors
        ${isSelected ? 'bg-primary/10 border-primary' : 'bg-card active:bg-muted'}
      `}
      onClick={() => {
        if (isSelecting) { onSelect?.(badge.id); return }
        onClick?.()
      }}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      {isSelecting && (
        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
          isSelected ? 'bg-primary border-primary' : 'border-muted-foreground'
        }`}>
          {isSelected && <Check size={12} className="text-primary-foreground" />}
        </div>
      )}

      {badge.badge_image_url ? (
        <img src={badge.badge_image_url} alt={badge.badge_name}
          className="w-12 h-12 rounded object-contain shrink-0" />
      ) : (
        <div className="w-12 h-12 rounded bg-muted flex items-center justify-center shrink-0">
          <Award size={24} className="text-muted-foreground" />
        </div>
      )}

      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">{badge.badge_name || 'バッジ'}</p>
        {badge.issuer_name && (
          <p className="text-xs text-muted-foreground truncate">{badge.issuer_name}</p>
        )}
        {badge.issued_on && (
          <p className="text-xs text-muted-foreground">
            {new Date(badge.issued_on).toLocaleDateString('ja-JP')}
          </p>
        )}
      </div>

      {!isSelecting && (
        <Button size="icon-sm" variant="ghost" className="bg-destructive/10 text-destructive hover:bg-destructive/20 shrink-0"
          onClick={(e) => { e.stopPropagation(); setConfirmOpen(true) }}>
          <Trash2 size={16} />
        </Button>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="バッジを削除しますか？"
        description={`「${badge.badge_name || 'このバッジ'}」を削除します。この操作は元に戻せません。`}
        confirmLabel="削除する"
        variant="destructive"
        onConfirm={() => onDelete(badge.id)}
      />
    </div>
  )
}
